import React, {
    useEffect,
    useState,
    useCallback,
    createContext,
    useContext,
    useMemo,
} from 'react';
import useAutomatedInsights from '../hooks/automatedInsights/useAutomatedInsights';

import SmvUpIconV2 from '../../images/smv_trending_up_2.svg';
import SmvDownIconV2 from '../../images/smv_trending_down_2.svg';
import MvpUpIconV2 from '../../images/mvp_trending_up_2.svg';
import MvpDownIconV2 from '../../images/mvp_trending_down_2.svg';

import * as R from 'ramda';
import {
    MVP_BLUEPRINT_ID,
    SMV_RS_BLUEPRINT_NAME,
    SMV_RSP_BLUEPRINT_ID,
} from './constants';

export const AutomatedInsightsContext = createContext<{
    insights: any[];
    allInsights: any[];
    isLoading: boolean;
    page: number;
    pageNumber: number;
    next: () => void;
    prev: () => void;
    selectedInsight: any;
    setSelectedInsight: (insight: any) => void;
    setPage: (page: number) => void;
    getIcon: (insight: any) => React.ReactNode;
    colors: readonly [string, string];
    filterTypes: any;
    setFilterTypes: (filterTypes: any) => void;
}>({
    insights: [],
    allInsights: [],
    isLoading: false,
    page: 1,
    pageNumber: 1,
    next: () => {},
    prev: () => {},
    selectedInsight: null,
    setSelectedInsight: () => {},
    setPage: () => {},
    getIcon: () => <></>,
    colors: ['#000', '#000'],
    filterTypes: {},
    setFilterTypes: () => {},
});

type AutomatedInsightsProviderProps = {
    children: React.ReactNode;
    user: any;
    client: any;
    filters: any;
};

export const PAGE_SIZE = 6;

export const AutomatedInsightsProvider = ({
    children,
    user,
    client,
    filters,
}: AutomatedInsightsProviderProps) => {
    const { data: insights, isLoading } = useAutomatedInsights({
        user,
        client,
    });

    const [filterTypes, setFilterTypes] = useState<any>([]);

    const colors = [client.primaryColor, client.secondaryColor] as const;

    const [page, setPage] = useState(1);
    const filteredInsightsRef = React.useRef<any[]>([]);
    const [selectedInsight, setSelectedInsight] = useState(null);

    const selected = {
        rightsholders: filters.rightsholders.selected,
        sponsors: filters.sponsors.selected,
        placements: filters.placements.selected,
    };

    useEffect(() => {
        if (!insights || isLoading) {
            filteredInsightsRef.current = [];
            return;
        }

        let filtered = insights;

        if (
            selected.rightsholders.length ||
            selected.sponsors.length ||
            selected.placements.length ||
            filterTypes.length > 0 // Check if filterTypes array is not empty
        ) {
            filtered = insights.filter(insight => {
                const rhIdentifier = insight.identifiers.find(
                    identifier => identifier.entity === 'rightsholders',
                );
                const spIdentifier = insight.identifiers.find(
                    identifier => identifier.entity === 'sponsors',
                );
                const plIdentifier = insight.identifiers.find(
                    identifier =>
                        identifier.entity === 'placements' ||
                        identifier.entity === 'placement_types',
                );

                const blueprint_id = insight.blueprint_id;

                const rhId = rhIdentifier ? rhIdentifier.id : null;
                const spId = spIdentifier ? spIdentifier.id : null;
                const pltId = plIdentifier ? plIdentifier.id : null;

                const plId = pltId ? `${rhId}-${pltId}` : null;
                const rhFilter = rhId
                    ? !selected.rightsholders.length ||
                      selected.rightsholders.includes(rhId)
                    : true;
                const spFilter = spId
                    ? !selected.sponsors.length ||
                      selected.sponsors.includes(spId)
                    : true;
                const plFilter = plId
                    ? !selected.placements.length ||
                      selected.placements.includes(plId)
                    : true;

                // Check if filterTypes is empty or blueprint_id is in filterTypes array
                const blueprintFilter =
                    filterTypes.length === 0 ||
                    filterTypes.includes(blueprint_id);

                switch (blueprint_id) {
                    case SMV_RSP_BLUEPRINT_ID:
                        return (
                            rhFilter && spFilter && plFilter && blueprintFilter
                        );
                    case SMV_RS_BLUEPRINT_NAME:
                        return (
                            rhFilter &&
                            spFilter &&
                            blueprintFilter &&
                            !selected.placements.length
                        );
                    case MVP_BLUEPRINT_ID:
                        return (
                            rhFilter && spFilter && plFilter && blueprintFilter
                        );
                    default:
                        return false;
                }
            });
        }

        filtered = filtered.sort(
            (a, b) => parseFloat(b.score) - parseFloat(a.score),
        );

        if (!R.equals(filteredInsightsRef.current, filtered)) {
            filteredInsightsRef.current = filtered;
            setSelectedInsight(filteredInsightsRef.current[0]);
            setPage(1); // Reset page when filters change
        }
    }, [insights, selected, filterTypes]); // Include filterTypes in the dependency array

    const paginatedInsights = useMemo(
        () =>
            filteredInsightsRef.current.slice(
                (page - 1) * PAGE_SIZE,
                page * PAGE_SIZE,
            ),
        [filteredInsightsRef.current, page],
    );

    const pageNumber = Math.ceil(
        filteredInsightsRef.current.length / PAGE_SIZE,
    );

    useEffect(() => {
        if (isLoading) return;
        setSelectedInsight(paginatedInsights[0]);
    }, [page]);

    const next = () => setPage(p => Math.min(p + 1, pageNumber));
    const prev = () => setPage(p => Math.max(p - 1, 1));

    const getIcon = useCallback((insight: any) => {
        switch (`${insight.icon}_${insight.blueprint_id}`) {
            case 'up_1':
                return SmvUpIconV2;
            case 'up_2':
                return SmvUpIconV2;
            case 'down_1':
                return SmvDownIconV2;
            case 'down_2':
                return SmvDownIconV2;
            case 'up_3':
                return MvpUpIconV2;
            case 'down_3':
                return MvpDownIconV2;
            default:
                return <></>;
        }
    }, []);

    return (
        <AutomatedInsightsContext.Provider
            value={{
                insights: paginatedInsights,
                allInsights: insights,
                isLoading,
                page,
                pageNumber,
                next,
                prev,
                selectedInsight,
                setSelectedInsight,
                setPage,
                getIcon,
                colors,
                filterTypes,
                setFilterTypes,
            }}
        >
            {children}
        </AutomatedInsightsContext.Provider>
    );
};

export const useAutomatedInsightsContext = () =>
    useContext(AutomatedInsightsContext);

export default AutomatedInsightsProvider;
